/* Mock data for POD Client development and testing */

import dayjs from 'dayjs';
import {
  PodConfigurationModel,
  PredictedDemandModel,
  PredictedDemandItemModel,
  StoreConfiguration,
  PodConfigurationLevel,
} from './podTypes';

/* ---- ID Generators ---- */
export const idGenerators = {
  configurationId: 100,
  nextConfigurationId(): number {
    return ++this.configurationId;
  },
};

/* ---- Mock Store Configurations ---- */
export const mockStoreConfigurations: Map<string, StoreConfiguration> = new Map([
  ['Starbucks:STORE001', { isPodEnabled: true }],
  ['Starbucks:STORE002', { isPodEnabled: true }],
  ['Starbucks:STORE003', { isPodEnabled: false }],
  ['CostaCoffee:CC001', { isPodEnabled: true }],
  ['CostaCoffee:CC002', { isPodEnabled: false }],
  ['Burger Palace:001', { isPodEnabled: true }],
  ['Burger Palace:002', { isPodEnabled: true }],
  ['Burger Palace:003', { isPodEnabled: false }],
  ['Burger Palace:004', { isPodEnabled: true }],
  ['Burger Palace:005', { isPodEnabled: false }],
]);

/* ---- Mock POD Configurations ---- */
// These configurations reference items from the orderManagementClient mocked items
// Item internal IDs: MILK-2L, BREAD-WHITE, COFFEE-1KG, SUGAR-1KG, ICECREAM-2L
export const mockConfigurations: PodConfigurationModel[] = [
  {
    id: 1,
    storeNumber: 'STORE001',
    storeName: 'Downtown Coffee Shop',
    starbucksStoreNumber: 'SB001',
    itemNumber: 'MILK-2L', // References SKU-MILK-001 from orderManagementClient
    itemDescription: 'Whole Milk 2L',
    franchiseName: 'Starbucks',
    minStockOnHandQuantity: 5,
    maxStockOnHandQuantity: 20,
    safetyStock: 3,
    isParEnabled: true,
    configurationLevel: PodConfigurationLevel.Store,
    enabled: true,
    franchiseStores: [
      { storeNumber: 'STORE001', starbucksStoreNumber: 'SB001' },
    ],
  },
  {
    id: 2,
    storeNumber: 'STORE001',
    storeName: 'Downtown Coffee Shop',
    starbucksStoreNumber: 'SB001',
    itemNumber: 'COFFEE-1KG', // References SKU-COFFEE-001 from orderManagementClient
    itemDescription: '1kg Arabica Coffee Beans',
    franchiseName: 'Starbucks',
    minStockOnHandQuantity: 10,
    maxStockOnHandQuantity: 50,
    safetyStock: 5,
    isParEnabled: true,
    configurationLevel: PodConfigurationLevel.Store,
    enabled: true,
    franchiseStores: [
      { storeNumber: 'STORE001', starbucksStoreNumber: 'SB001' },
    ],
  },
  {
    id: 3,
    storeNumber: 'STORE002',
    storeName: 'Airport Terminal',
    starbucksStoreNumber: 'SB002',
    itemNumber: 'MILK-2L', // References SKU-MILK-001 from orderManagementClient
    itemDescription: 'Whole Milk 2L',
    franchiseName: 'Starbucks',
    minStockOnHandQuantity: 10,
    maxStockOnHandQuantity: 40,
    safetyStock: 5,
    isParEnabled: true,
    configurationLevel: PodConfigurationLevel.Store,
    enabled: true,
    franchiseStores: [
      { storeNumber: 'STORE002', starbucksStoreNumber: 'SB002' },
    ],
  },
  {
    id: 4,
    storeNumber: '',
    storeName: '',
    starbucksStoreNumber: '',
    itemNumber: 'SUGAR-1KG', // References SKU-SUGAR-001 from orderManagementClient
    itemDescription: '1kg White Sugar',
    franchiseName: 'Starbucks',
    minStockOnHandQuantity: 2,
    maxStockOnHandQuantity: 10,
    safetyStock: 1,
    isParEnabled: false,
    configurationLevel: PodConfigurationLevel.Franchise,
    enabled: true,
    franchiseStores: [],
  },
  {
    id: 5,
    storeNumber: 'CC001',
    storeName: 'Costa High Street',
    starbucksStoreNumber: '',
    itemNumber: 'MILK-2L', // References SKU-MILK-001 from orderManagementClient
    itemDescription: 'Whole Milk 2L',
    franchiseName: 'CostaCoffee',
    minStockOnHandQuantity: 8,
    maxStockOnHandQuantity: 30,
    safetyStock: 4,
    isParEnabled: true,
    configurationLevel: PodConfigurationLevel.Store,
    enabled: true,
    franchiseStores: [
      { storeNumber: 'CC001', starbucksStoreNumber: '' },
    ],
  },
  {
    id: 6,
    storeNumber: 'STORE001',
    storeName: 'Downtown Coffee Shop',
    starbucksStoreNumber: 'SB001',
    itemNumber: 'BREAD-WHITE', // References SKU-BREAD-001 from orderManagementClient
    itemDescription: 'White Sliced Bread 800g',
    franchiseName: 'Starbucks',
    minStockOnHandQuantity: 3,
    maxStockOnHandQuantity: 15,
    safetyStock: 2,
    isParEnabled: true,
    configurationLevel: PodConfigurationLevel.Store,
    enabled: true,
    franchiseStores: [
      { storeNumber: 'STORE001', starbucksStoreNumber: 'SB001' },
    ],
  },
  {
    id: 7,
    storeNumber: 'STORE002',
    storeName: 'Airport Terminal',
    starbucksStoreNumber: 'SB002',
    itemNumber: 'COFFEE-1KG', // References SKU-COFFEE-001 from orderManagementClient
    itemDescription: '1kg Arabica Coffee Beans',
    franchiseName: 'Starbucks',
    minStockOnHandQuantity: 15,
    maxStockOnHandQuantity: 60,
    safetyStock: 8,
    isParEnabled: true,
    configurationLevel: PodConfigurationLevel.Store,
    enabled: true,
    franchiseStores: [
      { storeNumber: 'STORE002', starbucksStoreNumber: 'SB002' },
    ],
  },
  {
    id: 8,
    storeNumber: 'STORE003',
    storeName: 'Mall Location',
    starbucksStoreNumber: 'SB003',
    itemNumber: 'ICECREAM-2L', // References SKU-ICECREAM-001 from orderManagementClient
    itemDescription: 'Vanilla Ice Cream 2L',
    franchiseName: 'Starbucks',
    minStockOnHandQuantity: 2,
    maxStockOnHandQuantity: 12,
    safetyStock: 1,
    isParEnabled: true,
    configurationLevel: PodConfigurationLevel.Store,
    enabled: true,
    franchiseStores: [
      { storeNumber: 'STORE003', starbucksStoreNumber: 'SB003' },
    ],
  },
  {
    id: 9,
    storeNumber: 'CC001',
    storeName: 'Costa High Street',
    starbucksStoreNumber: '',
    itemNumber: 'COFFEE-1KG', // References SKU-COFFEE-001 from orderManagementClient
    itemDescription: '1kg Arabica Coffee Beans',
    franchiseName: 'CostaCoffee',
    minStockOnHandQuantity: 12,
    maxStockOnHandQuantity: 45,
    safetyStock: 6,
    isParEnabled: true,
    configurationLevel: PodConfigurationLevel.Store,
    enabled: true,
    franchiseStores: [
      { storeNumber: 'CC001', starbucksStoreNumber: '' },
    ],
  },
  {
    id: 10,
    storeNumber: 'CC002',
    storeName: 'Costa Train Station',
    starbucksStoreNumber: '',
    itemNumber: 'BREAD-WHITE', // References SKU-BREAD-001 from orderManagementClient
    itemDescription: 'White Sliced Bread 800g',
    franchiseName: 'CostaCoffee',
    minStockOnHandQuantity: 5,
    maxStockOnHandQuantity: 20,
    safetyStock: 3,
    isParEnabled: true,
    configurationLevel: PodConfigurationLevel.Store,
    enabled: true,
    franchiseStores: [
      { storeNumber: 'CC002', starbucksStoreNumber: '' },
    ],
  },
  // Burger Palace stores
  {
    id: 11,
    storeNumber: '001',
    storeName: 'Burger Palace Downtown',
    starbucksStoreNumber: '',
    itemNumber: 'BREAD-WHITE', // References SKU-BREAD-001 from orderManagementClient
    itemDescription: 'White Sliced Bread 800g',
    franchiseName: 'Burger Palace',
    minStockOnHandQuantity: 10,
    maxStockOnHandQuantity: 40,
    safetyStock: 5,
    isParEnabled: true,
    configurationLevel: PodConfigurationLevel.Store,
    enabled: true,
    franchiseStores: [
      { storeNumber: '001', starbucksStoreNumber: '' },
    ],
  },
  {
    id: 12,
    storeNumber: '004',
    storeName: 'Burger Palace Highway',
    starbucksStoreNumber: '',
    itemNumber: 'MILK-2L', // References SKU-MILK-001 from orderManagementClient
    itemDescription: 'Whole Milk 2L',
    franchiseName: 'Burger Palace',
    minStockOnHandQuantity: 8,
    maxStockOnHandQuantity: 25,
    safetyStock: 4,
    isParEnabled: true,
    configurationLevel: PodConfigurationLevel.Store,
    enabled: true,
    franchiseStores: [
      { storeNumber: '004', starbucksStoreNumber: '' },
    ],
  },
  {
    id: 13,
    storeNumber: '004',
    storeName: 'Burger Palace Highway',
    starbucksStoreNumber: '',
    itemNumber: 'ICECREAM-2L', // References SKU-ICECREAM-001 from orderManagementClient
    itemDescription: 'Vanilla Ice Cream 2L',
    franchiseName: 'Burger Palace',
    minStockOnHandQuantity: 5,
    maxStockOnHandQuantity: 20,
    safetyStock: 3,
    isParEnabled: true,
    configurationLevel: PodConfigurationLevel.Store,
    enabled: true,
    franchiseStores: [
      { storeNumber: '004', starbucksStoreNumber: '' },
    ],
  },
];

/* ---- Mock Predicted Demand Data ---- */

/**
 * Generate realistic predictions based on item configuration and day of week.
 * Higher demand on weekdays for coffee/bread, consistent demand for milk, lower for frozen items.
 */
function generateMockPredictions(
  itemNumbers: string[],
  dates: Date[],
  storeNumber: string,
): PredictedDemandModel[] {
  return itemNumbers.map(itemNumber => {
    // Get configuration for this item/store to use min/max/safety stock
    const config = mockConfigurations.find(
      c => c.itemNumber === itemNumber && c.storeNumber === storeNumber,
    );

    const predictedDemandItems: PredictedDemandItemModel[] = dates.map(date => {
      const dayOfWeek = dayjs(date).day(); // 0 = Sunday, 6 = Saturday
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      // Base demand varies by item type
      let baseDemand = 10;
      let variance = 5;
      
      switch (itemNumber) {
        case 'MILK-2L':
          baseDemand = 15;
          variance = 8;
          break;
        case 'COFFEE-1KG':
          baseDemand = isWeekend ? 8 : 20; // Higher on weekdays
          variance = 6;
          break;
        case 'BREAD-WHITE':
          baseDemand = isWeekend ? 6 : 12; // Higher on weekdays
          variance = 4;
          break;
        case 'SUGAR-1KG':
          baseDemand = 4;
          variance = 2;
          break;
        case 'ICECREAM-2L':
          baseDemand = isWeekend ? 10 : 6; // Higher on weekends
          variance = 3;
          break;
      }

      // Add some randomness but keep it realistic
      const predictedDemandQuantity = Math.max(
        1,
        Math.floor(baseDemand + (Math.random() - 0.5) * variance * 2),
      );

      // Current quantity is predicted demand adjusted slightly
      const quantity = Math.max(
        0,
        Math.floor(predictedDemandQuantity + (Math.random() - 0.5) * 3),
      );

      // Safety stock from configuration or default
      const safetyStock = config?.safetyStock 
        ? (typeof config.safetyStock === 'number' ? config.safetyStock : parseInt(config.safetyStock as string, 10))
        : Math.floor(baseDemand * 0.2);

      return {
        quantity,
        date,
        hasPrediction: true,
        predictedDemandQuantity,
        safetyStock,
      };
    });

    return {
      itemNumber,
      predictionInfo: {},
      predictedDemandItems,
    };
  });
}

/* ---- Helper to get mock predictions ---- */
export function getMockPredictions(
  storeNumber: string,
  dates: string[],
  itemNumbers?: string[],
): PredictedDemandModel[] {
  // Convert date strings to Date objects
  const parsedDates = dates.map(d => new Date(d));

  // If no specific items, get all items for the store
  const items = itemNumbers || mockConfigurations
    .filter(c => c.storeNumber === storeNumber)
    .map(c => c.itemNumber);

  return generateMockPredictions(items, parsedDates, storeNumber);
}
